import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCircle, XCircle, AlertCircle, Send, Link2, ChevronLeft, Smartphone } from 'lucide-react';
import toast from 'react-hot-toast';
import { useApi } from '../../utils/useApi';
import { GympeChatHeader, DocumentPreviewCard } from './WhatsAppPreview';

// Trigger events that can never both be active for a gym at once — enabling
// one auto-disables its sibling server-side (see automaticTemplateService.ts).
// Mirrored here purely for UI messaging; the backend is the source of truth.
const MUTUALLY_EXCLUSIVE_TRIGGERS: Record<string, string> = {
    payment_received: 'payment_received_with_file',
    payment_received_with_file: 'payment_received'
};

interface AutomaticTemplate {
    id: string;
    triggerEvent: string;
    title: string;
    templateName: string;
    content: string;
    isEnabled: boolean;
    category?: string | null;
    pricePerMessage?: string | number | null;
    headerType?: string | null;
    sampleParams?: Record<string, any> | null;
    parameterSchema?: Record<string, any> | null;
}

interface GymDetails {
    name: string;
    phoneNumbers: string[];
    notificationName?: string;
}

interface AutomaticNotificationsSettingsProps {
    gymId: string;
}

const WhatsAppPreview = ({ content, templateName, gymDetails, headerType, sampleParams, parameterSchema }: { content: string; templateName?: string; gymDetails?: GymDetails; headerType?: string | null; sampleParams?: Record<string, any> | null; parameterSchema?: Record<string, any> | null }) => {
    // Mock data for placeholders
    const getMockData = () => {
        const gymName = gymDetails?.notificationName || gymDetails?.name || 'FitLife Gym';
        const phone = gymDetails?.phoneNumbers?.[0] || '+91 98765 43210';

        // Positional fallback for legacy templates that carry no schema/sample data.
        const data: Record<string, string> = {
            '1': 'Rudy',
            '2': `Basic Plan at ${gymName}`,
            '3': '₹2000',
            '4': '24-Mar-2026',
            '5': '25 Dec 2025',
            '6': '₹12,000',
            '7': 'INV-2025-001',
            '8': '7'
        };

        // If it looks like a lead created template, use phone for param 3
        if (content.includes('reaching out') || content.includes('inquiry')) {
            data['3'] = phone;
        }

        // Any sample values authored on the template fill positions first —
        // notably custom keys the semantic map below doesn't recognize.
        if (sampleParams) {
            Object.entries(sampleParams).forEach(([pos, val]: [string, any]) => {
                if (val !== null && val !== undefined && val !== '') data[pos] = String(val);
            });
        }

        // When the template declares what each position means, fill each {{n}}
        // with a sample of the right *kind* — so a trial template's {{2}} shows
        // days, not a plan name — and use live gym data where we have it (real
        // gym name/contact beats a placeholder). Handles both {key:'x'} and 'x'
        // schema shapes. This wins over sampleParams for recognized keys.
        const semanticSample: Record<string, string> = {
            member_name: 'Rudy',
            gym_name: gymName,
            gym_contact: phone,
            trial_days: '3',
            trial_end_date: '24-Mar-2026',
            amount: '₹2000',
            plan_name: 'Basic Plan',
            invoice_id: 'INV-2025-001'
        };
        if (parameterSchema) {
            Object.entries(parameterSchema).forEach(([pos, cfg]: [string, any]) => {
                const key = typeof cfg === 'string' ? cfg : cfg?.key;
                if (key && semanticSample[key] !== undefined) data[pos] = semanticSample[key];
            });
        }

        // OTP verification code must never render as a name — apply last.
        if (templateName?.toLowerCase().includes('otp') || content.includes('verification code')) {
            data['1'] = '482913';
        }

        return data;
    };

    const mockData = getMockData();

    const formatContent = (text: string) => {
        let formatted = text;
        Object.entries(mockData).forEach(([key, value]) => {
            formatted = formatted.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
        });

        // Handle WhatsApp formatting
        return formatted.split('\n').map((line, i) => {
            // Bold: *text* -> <strong>text</strong>
            const boldLine = line.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
            return <div key={i} dangerouslySetInnerHTML={{ __html: boldLine }} />;
        });
    };

    return (
        <div className="relative w-[320px] h-[580px] bg-[#e5ddd5] rounded-[40px] border-[8px] border-gray-800 shadow-2xl overflow-hidden mx-auto flex flex-col">
            {/* Chat header — real WhatsApp iOS look, not the old green Business-app bar */}
            <div className="h-16 bg-[#f6f3ee] border-b border-gray-200 flex items-center px-3 gap-1">
                <ChevronLeft className="h-6 w-6 text-blue-500 flex-shrink-0" />
                <GympeChatHeader compact />
            </div>

            {/* Chat Body */}
            <div className="flex-1 p-4 overflow-y-auto bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat">
                <div className="max-w-[85%] bg-white rounded-lg rounded-tl-none p-3 shadow-sm relative mb-2">
                    {headerType === 'document' && (
                        <DocumentPreviewCard filename={`Invoice-${(gymDetails?.name || 'SAMPLE').replace(/\s+/g, '').toUpperCase().slice(0, 8)}.pdf`} />
                    )}
                    <div className="text-[13px] text-gray-800 leading-relaxed">
                        {formatContent(content)}
                    </div>
                    <div className="text-[10px] text-gray-400 text-right mt-1">
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                </div>
            </div>

            {/* Input Area */}
            <div className="h-14 bg-gray-100 flex items-center px-4 gap-3">
                <div className="flex-1 h-9 bg-white rounded-full px-4 flex items-center text-gray-400 text-sm">
                    Type a message
                </div>
                <div className="w-9 h-9 bg-[#128c7e] rounded-full flex items-center justify-center text-white">
                    <Send className="h-4 w-4" />
                </div>
            </div>
        </div>
    );
};

export default function AutomaticNotificationsSettings({ gymId }: AutomaticNotificationsSettingsProps) {
    const { fetchWithAuth } = useApi();
    const queryClient = useQueryClient();
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [previewTemplate, setPreviewTemplate] = useState<AutomaticTemplate | null>(null);

    // Fetch gym details
    const { data: gymData } = useQuery({
        queryKey: ['gym-details', gymId],
        queryFn: async () => {
            const response = await fetchWithAuth(`/api/gyms/${gymId}`);
            return response.data as GymDetails;
        },
        enabled: !!gymId
    });

    // Fetch automatic templates
    const { data: templatesData, isLoading, error } = useQuery({
        queryKey: ['automatic-templates', gymId],
        queryFn: async () => {
            return await fetchWithAuth(`/api/automatic-templates?gymId=${gymId}`);
        },
        enabled: !!gymId
    });

    const templates: AutomaticTemplate[] = templatesData?.data || [];

    const getSibling = (template: AutomaticTemplate) => {
        const siblingTrigger = MUTUALLY_EXCLUSIVE_TRIGGERS[template.triggerEvent];
        if (!siblingTrigger) return null;
        return templates.find(t => t.triggerEvent === siblingTrigger) || null;
    };

    // Toggle mutation
    const toggleMutation = useMutation({
        mutationFn: async ({ id, isEnabled }: { id: string; isEnabled: boolean }) => {
            setTogglingId(id);
            return await fetchWithAuth(`/api/automatic-templates/${id}/toggle`, {
                method: 'PATCH',
                body: JSON.stringify({ is_enabled: isEnabled })
            });
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ['automatic-templates', gymId] });
            setTogglingId(null);
            if (response?.disabledSiblingTitle) {
                toast(`"${response.disabledSiblingTitle}" was automatically disabled — only one of these two can be active at a time.`, { icon: 'ℹ️', duration: 5000 });
            }
        },
        onError: () => {
            setTogglingId(null);
            toast.error('Failed to update setting');
        }
    });

    const handleToggle = (template: AutomaticTemplate) => {
        toggleMutation.mutate({ id: template.id, isEnabled: !template.isEnabled });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading settings...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                    <span className="text-red-800">Failed to load settings. Please try again.</span>
                </div>
            </div>
        );
    }

    // The logs view moved to the top-level "Notification Logs" tab
    // (UnifiedNotificationLogs), which also covers campaign messages.
    return (
        <div className="space-y-6">
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start">
                            <Bell className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                            <div>
                                <h3 className="text-sm font-medium text-blue-900">Automatic Notifications</h3>
                                <p className="text-sm text-blue-700 mt-1">
                                    These notifications are sent automatically when specific events occur in the system. You can enable or disable them individually and preview how they look on WhatsApp.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Templates List */}
                        <div className="lg:col-span-2 space-y-4">
                            {templates.map((template) => (
                                <div
                                    key={template.id}
                                    className={`bg-white border rounded-xl p-6 transition-all cursor-pointer ${previewTemplate?.id === template.id
                                        ? 'border-blue-500 ring-1 ring-blue-500 shadow-md'
                                        : 'border-gray-200 hover:shadow-sm'
                                        }`}
                                    onClick={() => setPreviewTemplate(template)}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-lg font-medium text-gray-900">{template.title}</h3>
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${template.isEnabled
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {template.isEnabled ? (
                                                        <>
                                                            <CheckCircle className="h-3 w-3" />
                                                            Active
                                                        </>
                                                    ) : (
                                                        <>
                                                            <XCircle className="h-3 w-3" />
                                                            Disabled
                                                        </>
                                                    )}
                                                </span>
                                                {template.category && (
                                                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">
                                                        {template.category}
                                                    </span>
                                                )}
                                                {template.pricePerMessage != null && Number(template.pricePerMessage) > 0 && (
                                                    <span className="text-xs text-gray-500 font-medium">₹{Number(template.pricePerMessage).toFixed(2)}/msg</span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-500 mb-2">
                                                Trigger: <span className="font-mono bg-gray-100 px-1 py-0.5 rounded text-xs">{template.triggerEvent}</span>
                                            </p>

                                            {(() => {
                                                const sibling = getSibling(template);
                                                if (!sibling) return null;
                                                return (
                                                    <p className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mb-4">
                                                        <Link2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                                        <span>
                                                            Only one of <strong>"{template.title}"</strong> or <strong>"{sibling.title}"</strong> can be active at a time.
                                                            {!template.isEnabled && sibling.isEnabled && ` Enabling this will automatically disable "${sibling.title}".`}
                                                        </span>
                                                    </p>
                                                );
                                            })()}

                                            <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600 whitespace-pre-wrap">
                                                {template.content}
                                            </div>
                                        </div>

                                        <div className="ml-6 flex items-center">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleToggle(template);
                                                }}
                                                disabled={togglingId === template.id}
                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${template.isEnabled ? 'bg-blue-600' : 'bg-gray-200'
                                                    } ${togglingId === template.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                role="switch"
                                                aria-checked={template.isEnabled}
                                            >
                                                <span
                                                    aria-hidden="true"
                                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${template.isEnabled ? 'translate-x-5' : 'translate-x-0'
                                                        }`}
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {templates.length === 0 && (
                                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                    <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">No automatic notifications found</h3>
                                    <p className="text-gray-600">
                                        There are no automatic notification templates configured for your gym.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Preview Panel */}
                        <div className="lg:col-span-1">
                            <div className="sticky top-6">
                                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="font-bold text-gray-900">WhatsApp Preview</h3>
                                        {previewTemplate && (
                                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full font-medium">
                                                Live Preview
                                            </span>
                                        )}
                                    </div>

                                    {previewTemplate ? (
                                        <div className="animate-in fade-in zoom-in-95 duration-300">
                                            <WhatsAppPreview
                                                content={previewTemplate.content}
                                                templateName={previewTemplate.templateName}
                                                gymDetails={gymData}
                                                headerType={previewTemplate.headerType}
                                                sampleParams={previewTemplate.sampleParams}
                                                parameterSchema={previewTemplate.parameterSchema}
                                            />
                                            <p className="text-center text-xs text-gray-400 mt-4 italic">
                                                * Placeholders are populated with sample data for preview
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="h-[580px] border-2 border-dashed border-gray-100 rounded-[40px] flex flex-col items-center justify-center text-center p-8">
                                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                                <Smartphone className="h-8 w-8 text-gray-300" />
                                            </div>
                                            <p className="text-gray-500 text-sm">
                                                Select a notification template from the list to see how it looks on WhatsApp
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
        </div>
    );
}




